/*
 * Shared calendar building blocks — used internally by DatepickerRenderer and DaterangeRenderer.
 *
 * buildCalHeader(viewDate, onPrev, onNext)
 *   Returns a header <div> with prev/next month navigation buttons.
 *   Pass null for onPrev or onNext to hide/disable that arrow.
 *
 * buildCalGrid(viewDate, onDayClick, opts, navCbs)
 *   Returns a keyboard-navigable day grid for the given month.
 *   opts   : { selected, rangeStart, rangeEnd } — controls highlight classes on days
 *   navCbs : { goPrev, goNext } — called when arrow-key navigation crosses a month boundary
 *
 * buildTrigger(labelText, iconName)
 *   Returns the shared button trigger (icon + label + chevron) used by daterange and timerange.
 */
import { formatDateISO } from '../utils/dateUtils.js';

export const CalendarRenderer = {
    buildCalHeader(viewDate, onPrev, onNext) {
        const header = document.createElement('div');
        header.className = 'calHeader';

        const prevBtn = document.createElement('button');
        prevBtn.className = 'calNavBtn';
        prevBtn.type      = 'button';
        prevBtn.innerHTML = '<i class="fa-solid fa-chevron-left"></i>';
        prevBtn.setAttribute('aria-label', 'Vorheriger Monat');
        if (onPrev && !this.hasAttribute('disabled')) {
            prevBtn.addEventListener('click', e => { e.stopPropagation(); onPrev(); });
        } else {
            prevBtn.disabled         = true;
            prevBtn.style.visibility = 'hidden';
        }

        const label = document.createElement('span');
        label.className   = 'calMonthLabel';
        label.textContent = viewDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

        const nextBtn = document.createElement('button');
        nextBtn.className = 'calNavBtn';
        nextBtn.type      = 'button';
        nextBtn.innerHTML = '<i class="fa-solid fa-chevron-right"></i>';
        nextBtn.setAttribute('aria-label', 'Nächster Monat');
        if (onNext && !this.hasAttribute('disabled')) {
            nextBtn.addEventListener('click', e => { e.stopPropagation(); onNext(); });
        } else {
            nextBtn.disabled         = true;
            nextBtn.style.visibility = 'hidden';
        }

        header.appendChild(prevBtn);
        header.appendChild(label);
        header.appendChild(nextBtn);
        return header;
    },

    buildCalGrid(viewDate, onDayClick, { selected = null, rangeStart = null, rangeEnd = null } = {}, { goPrev, goNext } = {}) {
        const year  = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const today = formatDateISO(new Date());

        const grid = document.createElement('div');
        grid.className = 'calGrid';
        grid.setAttribute('role', 'grid');

        ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].forEach(day => {
            const cell = document.createElement('div');
            cell.className   = 'calWeekday';
            cell.textContent = day;
            cell.setAttribute('role', 'columnheader');
            grid.appendChild(cell);
        });

        let firstDay = new Date(year, month, 1).getDay();
        firstDay = firstDay === 0 ? 6 : firstDay - 1;
        for (let i = 0; i < firstDay; i++) {
            const empty = document.createElement('div');
            empty.className = 'calDay calDayEmpty';
            empty.setAttribute('role', 'gridcell');
            grid.appendChild(empty);
        }

        const minDate = this.getAttribute('min') || null;
        const maxDate = this.getAttribute('max') || null;

        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const dayCells    = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const cell    = document.createElement('div');
            cell.className    = 'calDay';
            cell.textContent  = day;
            cell.dataset.date = dateStr;
            cell.setAttribute('role', 'gridcell');
            cell.setAttribute('aria-label', new Date(year, month, day).toLocaleDateString('de-DE'));

            if (dateStr === today)      cell.classList.add('calToday');
            if (dateStr === selected)   cell.classList.add('calSelected');
            if (dateStr === rangeStart) cell.classList.add('calRangeStart');
            if (dateStr === rangeEnd)   cell.classList.add('calRangeEnd');

            if (rangeStart && rangeEnd) {
                const rangeLow  = rangeStart < rangeEnd ? rangeStart : rangeEnd;
                const rangeHigh = rangeStart < rangeEnd ? rangeEnd   : rangeStart;
                if (dateStr > rangeLow && dateStr < rangeHigh) cell.classList.add('calInRange');
            }

            const outOfRange = (minDate && dateStr < minDate) || (maxDate && dateStr > maxDate);
            const isDisabled = this.hasAttribute('disabled') || outOfRange;

            if (isDisabled) {
                cell.setAttribute('aria-disabled', 'true');
                cell.classList.add('calDisabled');
                cell.setAttribute('tabindex', '-1');
            } else if (onDayClick) {
                cell.setAttribute('tabindex', '-1');
                cell.addEventListener('click', e => { e.stopPropagation(); onDayClick(dateStr); });
                cell.addEventListener('keydown', e => {
                    switch (e.key) {
                        case 'Enter':
                        case ' ':
                            e.preventDefault();
                            e.stopPropagation();
                            onDayClick(dateStr);
                            break;
                        case 'Escape':
                            e.stopPropagation();
                            this.closePanel();
                            this.trigger?.focus();
                            break;
                        case 'ArrowRight':
                            e.preventDefault(); e.stopPropagation();
                            focusDay(dayCells, cell, 1, goNext);
                            break;
                        case 'ArrowLeft':
                            e.preventDefault(); e.stopPropagation();
                            focusDay(dayCells, cell, -1, goPrev);
                            break;
                        case 'ArrowDown':
                            e.preventDefault(); e.stopPropagation();
                            focusDay(dayCells, cell, 7, goNext);
                            break;
                        case 'ArrowUp':
                            e.preventDefault(); e.stopPropagation();
                            focusDay(dayCells, cell, -7, goPrev);
                            break;
                        case 'PageDown':
                            e.preventDefault(); e.stopPropagation();
                            goNext?.();
                            break;
                        case 'PageUp':
                            e.preventDefault(); e.stopPropagation();
                            goPrev?.();
                            break;
                    }
                });
                dayCells.push(cell);
            }

            grid.appendChild(cell);
        }

        return grid;
    },

    buildTrigger(labelText, iconName) {
        const trigger = document.createElement('button');
        trigger.className = 'inputBtn inputTrigger';
        trigger.type      = 'button';
        trigger.disabled  = this.hasAttribute('disabled');
        trigger.innerHTML = `
            <i class="fa-solid fa-${iconName}" aria-hidden="true"></i>
            <span class="inputTriggerLabel">${labelText}</span>
            <i class="fa-solid fa-chevron-down inputChevron" aria-hidden="true"></i>
        `;
        return trigger;
    },
};

function focusDay(dayCells, currentCell, delta, onOverflow) {
    const idx    = dayCells.indexOf(currentCell);
    const target = dayCells[idx + delta];
    if (target) {
        target.focus();
    } else if (onOverflow) {
        onOverflow();
    }
}
